import { h, Component } from 'preact';
import util from 'preact-util';
import { Text } from 'preact-i18n';

import localUtil from '../../lib/util';
import MarkdownDisplay from '../gui/markdownDisplay';
import ImageScroller from '../imagescroller';
import Likes from '../likes';
import { findQuotedMessage, quoteSnippet } from '../../lib/inboxReply';
import { TAP_THRESHOLD_MS, LONG_PRESS_MS, isDoubleTap, messageActions, readState, classifySwipe, swipeTranslateX, isReplyTriggered } from '../../lib/inboxTapGesture';

// Reaction icon set (mirrors the likes component) for the on-bubble reaction pill.
const REACTION_TYPES = [
    { type: 'like', icon: 'fa-thumbs-up', color: '#1877f2' },
    { type: 'love', icon: 'fa-heart', color: '#e74c3c' },
    { type: 'haha', icon: 'fa-face-laugh-squint', color: '#f1c40f' },
    { type: 'wow', icon: 'fa-face-surprise', color: '#f1c40f' },
    { type: 'sad', icon: 'fa-face-sad-tear', color: '#f1c40f' },
    { type: 'angry', icon: 'fa-face-angry', color: '#e67e22' },
    { type: 'stars', icon: 'fa-face-grin-stars', color: '#f1c40f' },
    { type: 'think', icon: 'fa-face-thinking', color: '#f1c40f' },
    { type: 'fire', icon: 'fa-fire-flame-curved', color: '#e67e22' },
];

// Up to the 3 most common reaction icons, for the on-bubble pill.
function reactionIcons(likes) {
    if (!likes || likes.length === 0) {
        return null;
    }
    const counts = likes.reduce((acc, like) => {
        const type = like.likeType || 'like';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([type]) => {
            const reaction = REACTION_TYPES.find(r => r.type === type);
            if (!reaction) {
                return null;
            }
            return (
                <i
                    key={type}
                    class={`fa-solid ${reaction.icon}`}
                    style={`color: ${reaction.color}; font-size: 0.65rem;`}
                />
            );
        });
}

class InboxMessage extends Component {
    constructor(props) {
        super(props);
        this.state = { showReactionPicker: false, swipeX: 0 };
        this.lastTapAt = 0;
        this.tapTimer = null;
        this.longPressTimer = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.swipeMode = false;
        this.gestureAborted = false;
        this.gestureHandled = false;
        this.pickerRef = null;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.showReactionPicker && !prevState.showReactionPicker) {
            document.addEventListener('click', this.handleOutsideClick, true);
            document.addEventListener('touchstart', this.handleOutsideClick, true);
        } else if (!this.state.showReactionPicker && prevState.showReactionPicker) {
            document.removeEventListener('click', this.handleOutsideClick, true);
            document.removeEventListener('touchstart', this.handleOutsideClick, true);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.tapTimer);
        clearTimeout(this.longPressTimer);
        document.removeEventListener('click', this.handleOutsideClick, true);
        document.removeEventListener('touchstart', this.handleOutsideClick, true);
    }

    handleOutsideClick = (e) => {
        if (this.pickerRef && !this.pickerRef.contains(e.target)) {
            this.setState({ showReactionPicker: false });
        }
    };

    handleTouchStart = (e) => {
        const { message, onReact } = this.props;
        if (message.pending || message.failed) {
            return;
        }
        const t = e.touches && e.touches[0];
        if (!t) {
            return;
        }
        this.touchStartX = t.clientX;
        this.touchStartY = t.clientY;
        this.swipeMode = false;
        this.gestureAborted = false;
        this.gestureHandled = false;
        clearTimeout(this.longPressTimer);
        if (onReact) {
            this.longPressTimer = setTimeout(() => {
                this.gestureHandled = true;
                this.setState({ showReactionPicker: true });
                if (navigator.vibrate) {
                    navigator.vibrate(40);
                }
            }, LONG_PRESS_MS);
        }
    };

    handleTouchMove = (e) => {
        const t = e.touches && e.touches[0];
        if (!t) {
            return;
        }
        const dx = t.clientX - this.touchStartX;
        const dy = t.clientY - this.touchStartY;
        if (!this.swipeMode && !this.gestureAborted) {
            const axis = classifySwipe(dx, dy);
            if (axis === 'vertical') {
                this.gestureAborted = true;
                clearTimeout(this.longPressTimer);
                return;
            }
            if (axis === 'horizontal') {
                if (!this.props.onReply) {
                    this.gestureAborted = true;
                    clearTimeout(this.longPressTimer);
                    return;
                }
                this.swipeMode = true;
                this.gestureHandled = true;
                clearTimeout(this.longPressTimer);
            }
        }
        if (this.swipeMode) {
            this.setState({ swipeX: swipeTranslateX(dx) });
        }
    };

    handleTouchEnd = () => {
        clearTimeout(this.longPressTimer);
        if (this.swipeMode) {
            const triggered = isReplyTriggered(this.state.swipeX);
            this.swipeMode = false;
            this.setState({ swipeX: 0 });
            if (triggered && this.props.onReply) {
                this.props.onReply(this.props.message);
            }
        }
    };

    handlePickReaction = (likeType) => {
        const { onReact, message } = this.props;
        this.setState({ showReactionPicker: false });
        if (onReact) {
            onReact(message, likeType);
        }
    };

    handleBubbleTap = (e) => {
        const { message, onQuickLike, onToggleExpand, onBodyLinkClick } = this.props;

        // A swipe or long-press already consumed this touch — suppress the
        // synthetic click so it doesn't also toggle the details strip.
        if (this.gestureHandled) {
            this.gestureHandled = false;
            return;
        }

        // Links in the markdown body navigate (and may close drawers); never treat
        // them as a like / details toggle.
        const link = e.target.closest('a');
        if (link) {
            if (onBodyLinkClick) {
                onBodyLinkClick(e);
            }
            return;
        }

        // Pending / failed messages have their own inline affordances.
        if (message.pending || message.failed) {
            return;
        }

        const now = Date.now();
        if (isDoubleTap(this.lastTapAt, now)) {
            clearTimeout(this.tapTimer);
            this.lastTapAt = 0;
            if (onQuickLike) {
                onQuickLike(message);
            }
            return;
        }

        this.lastTapAt = now;
        clearTimeout(this.tapTimer);
        this.tapTimer = setTimeout(() => {
            this.lastTapAt = 0;
            if (onToggleExpand) {
                onToggleExpand(message.uuidv4);
            }
        }, TAP_THRESHOLD_MS);
    };

    handleCopy = (e) => {
        e.stopPropagation();
        const { message } = this.props;
        const text = message.body || '';
        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text);
        }
    };

    render() {
        const {
            stores,
            message,
            messages = [],
            isOwnMessage,
            isBroadcast,
            language,
            expanded,
            onReply,
            onReact,
            onReplyToUser,
            onEdit,
            onDelete,
            onRetry,
            onImageClick,
        } = this.props;

        const meta = readState(message, isOwnMessage);
        const quoted = findQuotedMessage(messages, message.replyToUuidv4);
        const actions = messageActions({
            isOwnMessage,
            isBroadcast,
            has: {
                reply: Boolean(onReply),
                replyToUser: Boolean(onReplyToUser),
                edit: Boolean(onEdit),
                delete: Boolean(onDelete),
            },
        });
        const side = isOwnMessage ? 'end' : 'start';

        return (<div class='mb-3'>
            {isBroadcast && message.fromName && !isOwnMessage ? (
                <div class='d-flex flex-row w-100 justify-content-start' style='margin-bottom: -2px;'>
                    <small class='text-body-secondary fw-bold' style='font-size: 0.75em;'>
                        <i class='fa-solid fa-user me-1' style='font-size: 0.8em;' />
                        {message.fromName}
                    </small>
                </div>
            ) : null}

            <div class='d-flex flex-row w-100 justify-content-between position-relative'>
                {this.state.swipeX > 0 ? (
                    <div
                        class='position-absolute d-flex align-items-center'
                        style={`top: 0; bottom: 0; left: 8px; opacity: ${Math.min(this.state.swipeX / 60, 1)};`}
                    >
                        <i class='fa-solid fa-reply text-body-secondary' />
                    </div>
                ) : null}
                {this.state.showReactionPicker && onReact ? (
                    <div
                        ref={el => { this.pickerRef = el; }}
                        class={`reaction-picker position-absolute d-flex flex-row bg-body rounded-pill shadow-lg p-1 border ${isOwnMessage ? 'end-0' : 'start-0'}`}
                        style='bottom: 100%; z-index: 1000;'
                    >
                        {REACTION_TYPES.map(reaction => (
                            <button
                                key={reaction.type}
                                type='button'
                                class='btn btn-link p-1 px-2'
                                onClick={(e) => { e.stopPropagation(); e.preventDefault(); this.handlePickReaction(reaction.type); }}
                                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); this.handlePickReaction(reaction.type); }}
                            >
                                <i class={`fa-solid ${reaction.icon}`} style={`color: ${reaction.color}; font-size: 1.2em;`} />
                            </button>
                        ))}
                    </div>
                ) : null}
                <div
                    class={`commentBubble ${isOwnMessage ? 'myMessage' : 'othersComment'} position-relative pt-1 ${message.pending ? 'opacity-50' : ''} ${message.failed ? 'border border-danger' : ''}`}
                    onClick={this.handleBubbleTap}
                    onTouchStart={this.handleTouchStart}
                    onTouchMove={this.handleTouchMove}
                    onTouchEnd={this.handleTouchEnd}
                    style={`cursor: pointer; transform: translateX(${this.state.swipeX}px); transition: ${this.swipeMode ? 'none' : 'transform 0.15s ease'};`}
                    key={message.id}
                >
                    <div class='commentBody pt-1'>
                        {quoted ? (
                            <div class='border-start border-3 ps-2 mb-1 text-body-secondary small'>
                                {quoteSnippet(quoted.body)}
                            </div>
                        ) : null}
                        <MarkdownDisplay
                            markdown={localUtil.replaceImages(message.body, message.images, language, false)}
                            markedOpts={localUtil.getMarkdownOptions()}
                        />
                        {message.likes && message.likes.length > 0 ? (
                            <div
                                class='likesCount position-absolute d-flex align-items-center rounded-pill px-1 py-0 border'
                                style={`bottom: -8px; right: -10px; background-color: var(--bs-body-bg); font-size: 0.65rem; line-height: 1rem; gap: 2px;`}
                            >
                                {reactionIcons(message.likes)}
                                <span class='ms-1 text-body-secondary'>{message.likes.length}</span>
                            </div>
                        ) : null}
                    </div>
                </div>
            </div>

            {message.images && message.images.length > 0 ? (
                <div class={`d-flex justify-content-${side}`}>
                    <ImageScroller
                        images={message.images}
                        stores={stores}
                        showImg={img => localUtil.displayImg(img, this.props, '800x')}
                        showLargeImage={img => localUtil.displayImg(img, this.props, '1920x')}
                        maxHeight={'30vh'}
                        maxWidth={'270px'}
                        classNamesWrapper1={'mt-1'}
                        classNames={'mt-1'}
                        wrapperMaxWidth={'270px'}
                        onImageClick={() => onImageClick && onImageClick(message.images)}
                    />
                </div>
            ) : null}

            {message.pending ? (
                <div class={`d-flex flex-row w-100 justify-content-${side}`}>
                    <small class='text-body-secondary'>
                        <i class='fa-regular fa-clock me-1' /><Text id='inbox.sending'>Sending…</Text>
                    </small>
                </div>
            ) : message.failed ? (
                <div class={`d-flex flex-row w-100 justify-content-${side}`}>
                    <small class='text-danger' role='button' style='cursor: pointer;' onClick={() => onRetry && onRetry(message)}>
                        <i class='fa-solid fa-triangle-exclamation me-1' /><Text id='inbox.notSentRetry'>Not sent — tap to retry</Text>
                    </small>
                </div>
            ) : expanded ? (
                <div class={`inboxDetailsStrip d-flex flex-row align-items-center flex-wrap gap-2 w-100 justify-content-${side}`}>
                    <small class='text-body-secondary'>{localUtil.getTimeAgo(meta.timeValue)}</small>
                    {meta.isEdited ? (
                        <small class='text-body-secondary fst-italic'><Text id='inbox.edited'>(edited)</Text></small>
                    ) : null}
                    {meta.showRead ? (
                        meta.isRead ? (
                            <small class='text-primary'>
                                <i class='fa-solid fa-check-double me-1' />
                                <Text id='inbox.read'>Read</Text>{' '}
                                {util.formatDate(meta.readAt, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}
                            </small>
                        ) : (
                            <small class='text-body-secondary'><Text id='inbox.delivered'>Delivered</Text></small>
                        )
                    ) : null}

                    {actions.includes('reply') ? (
                        <button class='btn btn-link btn-sm text-primary p-0' onClick={() => onReply(message)}>
                            <i class='fa-solid fa-reply me-1' /><Text id='inbox.reply'>Reply</Text>
                        </button>
                    ) : null}
                    {actions.includes('replyToUser') ? (
                        <button class='btn btn-link btn-sm text-primary p-0' onClick={() => onReplyToUser(message)}>
                            <i class='fa-solid fa-reply me-1' /><Text id='inbox.replyTo'>Reply to</Text>
                        </button>
                    ) : null}
                    {actions.includes('react') ? (
                        <Likes
                            stores={stores}
                            type={'message'}
                            objectId={message.id}
                            uuidv4={message.uuidv4}
                            likes={message.likes}
                            showSimpleLike={true}
                            alignRight={isOwnMessage}
                            shareTitle={message.body}
                            shareSummary={message.body}
                            shareImage={message.images?.[0]?.s3SmallLink}
                        />
                    ) : null}
                    {actions.includes('copy') ? (
                        <button class='btn btn-link btn-sm text-body-secondary p-0' onClick={this.handleCopy}>
                            <i class='fa-regular fa-copy me-1' /><Text id='inbox.copy'>Copy</Text>
                        </button>
                    ) : null}
                    {actions.includes('edit') ? (
                        <button class='btn btn-link btn-sm text-body-secondary p-0' onClick={() => onEdit(message)}>
                            <i class='fa-duotone fa-pen' />
                        </button>
                    ) : null}
                    {actions.includes('delete') ? (
                        <button class='btn btn-link btn-sm text-body-secondary p-0' onClick={() => onDelete(message)}>
                            <i class='fa-regular fa-trash' />
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>);
    }
}

export default InboxMessage;
